import { DashboardLayout } from 'app/layout'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from 'shared/components/Button'
import { TabsWrapper, WrapperSearch } from './style'
import { Tab, Tabs } from 'shared/components/Tabs'
import { Input } from 'shared/components/Input'
import { Table } from 'shared/components/Table'
import { useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { axiosApi } from 'shared/api/axios'
import { columnBlackList } from './model/columnBlackList'
import { useDebounce } from 'shared/hooks/useDebounce'
import { columns } from './model/columns'
import { getClients } from './api/getClients'
import { useDispatch } from 'react-redux'
import { closeModal, openModal } from 'shared/redux/slice/modalSlice'
import { showNotification } from 'shared/redux/slice/notificationSlice'
import { SortingState } from '@tanstack/react-table'
import { columnsOrg } from './model/columnOrg'
import { CLIENTSCREATE, CLIENTSPROFILE } from 'shared/consts/routingConsts'
import { Loader } from 'shared/components/Loader'
import { CounterpartiesApi } from 'entities/counterparties/api'
import useDownloadFile from 'shared/hooks/useDownloadFile'

export const Clients = () => {
    const [searchParams, setSearchParams] = useSearchParams()
    const queryClient = useQueryClient()
    const dispatch = useDispatch()
    const { downloadFile } = useDownloadFile()

    const previousTabParam = searchParams.get('activeTab')
    const previousTab =
        previousTabParam && !Number.isNaN(Number(previousTabParam))
            ? Number(previousTabParam)
            : 0
    const [tab, setTab] = useState<number>(previousTab)
    const [typeClient, setTypeClient] = useState(previousTab)

    const [search, setSearch] = useState<string>('')

    const [loading, setLoading] = useState<boolean>(false)

    const currentPage = parseInt(searchParams.get('page') || '1', 10)
    const [sorting, setSorting] = useState<SortingState>([])

    const navigate = useNavigate()

    const debouncedSearchTerm = useDebounce(search, 1000)
    const handleClick = (to: string) => {
        navigate(to)
    }

    const [selectedRows, setSelectedRows] = useState<any[]>([])

    const handleSelectionChange = (newSelectedRows: any[]) => {
        setSelectedRows(newSelectedRows)
    }

    const hasSelectedRows = useMemo(() => {
        return selectedRows.length > 0
    }, [selectedRows])

    const stableSorting = useMemo(() => {
        return sorting.length ? sorting : []
    }, [sorting])

    const { isLoading, isError, data, isFetching, isPending, refetch } =
        useQuery({
            queryKey: [
                'getClients',
                currentPage,
                typeClient,
                search,
                stableSorting,
            ],
            queryFn: () => {
                const order_by = stableSorting.length
                    ? stableSorting[0].id
                    : 'created_at'
                const order_asc = stableSorting.length
                    ? stableSorting[0].desc
                    : false
                return getClients(
                    typeClient,
                    currentPage,
                    search,
                    order_by,
                    order_asc,
                )
            },
            enabled: debouncedSearchTerm !== null,
            retry: 3,
        })

    const handleDeleteClients = async (selectedRows: any[]) => {
        const arraySelectedClients = selectedRows.map(item => item.id)
        try {
            const response = await axiosApi.delete(
                `${process.env.API_BACKEND}/v1/counterparties/counterparty/`,
                {
                    data: {
                        counterparty_ids: arraySelectedClients,
                    },
                },
            )
            if (response.status === 204) {
                dispatch(closeModal())
                dispatch(
                    showNotification({ message: 'Успешно!', type: 'success' }),
                )
                setSelectedRows([])
                refetch()
            }
        } catch (error) {
            console.log(error)
            throw error
        } finally {
            setLoading(false)
        }
    }

    const handleAddBlacList = async (selectedRows: any[], comment?: string) => {
        const arraySelectedClients = selectedRows.map(item => item.id)
        dispatch(closeModal())
        try {
            const response = await axiosApi.post(
                `${process.env.API_BACKEND}/v1/counterparties/counterparty/black_list/`,
                {
                    counterparty_ids: arraySelectedClients,
                    comment: comment,
                },
            )
            if (response.status === 204) {
                dispatch(
                    showNotification({ message: 'Успешно!', type: 'success' }),
                )
                setSelectedRows([])
                refetch()
            }
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    const handleRemoveBlacList = async (selectedRows: any[]) => {
        const arraySelectedClients = selectedRows.map(item => item.id)
        dispatch(closeModal())
        try {
            const response = await axiosApi.delete(
                `${process.env.API_BACKEND}/v1/counterparties/counterparty/black_list/`,
                {
                    data: {
                        counterparty_ids: arraySelectedClients,
                        comment: '',
                    },
                },
            )
            if (response.status === 204) {
                dispatch(
                    showNotification({ message: 'Успешно!', type: 'success' }),
                )
                setSelectedRows([])
                refetch()
            }
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    const handleTabChange = async (index: number) => {
        setTypeClient(index)
        setSearch('')
        if (isFetching || isPending) {
            await queryClient.cancelQueries({ queryKey: ['getClients'] })
        }
        handleSelectionChange([])
        setTab(index)
    }

    const onSearch = (e: any) => {
        setSearch(e.target.value)
    }

    const exportAnalytic = async () => {
        const { uri, filename } = await CounterpartiesApi.exportAnalytics()
        await downloadFile(uri, filename)
    }

    return (
        <>
            <DashboardLayout
                header={
                    <div className='dashboard__header'>
                        <div className='dashboard__header-title'>
                            Управление клиентами
                        </div>

                        <div className='dashboard__right-menu'>
                            <Button
                                themeType='outline-2'
                                requiredPermission='save'
                                onClick={exportAnalytic}
                                afterIcon='download'
                            >
                                Экспорт&nbsp;
                            </Button>
                            {hasSelectedRows ? (
                                <>
                                    {typeClient !== 2 ? (
                                        <Button
                                            requiredPermission='add'
                                            themeType='outline-2'
                                            onClick={() => {
                                                let localComment = ''
                                                dispatch(
                                                    openModal({
                                                        isOpen: true,
                                                        content: (
                                                            <div className='blockWithComment'>
                                                                Вы уверены, что
                                                                хотите добавить
                                                                клиентов:
                                                                <br />
                                                                <b>
                                                                    {tab ===
                                                                    0 ? (
                                                                        <>
                                                                            {selectedRows
                                                                                .map(
                                                                                    item => {
                                                                                        const contact =
                                                                                            item.contact
                                                                                        return [
                                                                                            contact.surname,
                                                                                            contact.first_name,
                                                                                            contact.patronymic,
                                                                                        ]
                                                                                            .filter(
                                                                                                Boolean,
                                                                                            )
                                                                                            .join(
                                                                                                ' ',
                                                                                            )
                                                                                    },
                                                                                )
                                                                                .filter(
                                                                                    Boolean,
                                                                                )
                                                                                .join(
                                                                                    ', ',
                                                                                )}
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            {selectedRows.map(
                                                                                (
                                                                                    item,
                                                                                    key,
                                                                                ) => (
                                                                                    <div
                                                                                        key={
                                                                                            key
                                                                                        }
                                                                                    >
                                                                                        {item
                                                                                            .additional_data
                                                                                            ?.documents
                                                                                            ?.organization_name
                                                                                            ? item
                                                                                                  .additional_data
                                                                                                  .documents
                                                                                                  .organization_name
                                                                                            : [
                                                                                                  item
                                                                                                      .contact
                                                                                                      ?.surname,
                                                                                                  item
                                                                                                      .contact
                                                                                                      ?.first_name,
                                                                                                  item
                                                                                                      .contact
                                                                                                      ?.patronymic,
                                                                                              ]
                                                                                                  .filter(
                                                                                                      Boolean,
                                                                                                  )
                                                                                                  .join(
                                                                                                      ' ',
                                                                                                  )}
                                                                                    </div>
                                                                                ),
                                                                            )}
                                                                        </>
                                                                    )}
                                                                </b>{' '}
                                                                в черный список?
                                                                <textarea
                                                                    placeholder='Комментарий'
                                                                    onChange={e =>
                                                                        (localComment =
                                                                            e
                                                                                .target
                                                                                .value)
                                                                    }
                                                                />
                                                            </div>
                                                        ),
                                                        config: {
                                                            title: 'Вы точно хотите добавить в ЧС?',
                                                            btns: [
                                                                {
                                                                    onClick:
                                                                        () =>
                                                                            handleAddBlacList(
                                                                                selectedRows,
                                                                                localComment,
                                                                            ),
                                                                    title: 'Да',
                                                                    active: true,
                                                                },
                                                                {
                                                                    onClick:
                                                                        () =>
                                                                            dispatch(
                                                                                closeModal(),
                                                                            ),
                                                                    title: 'Нет',
                                                                    active: false,
                                                                },
                                                            ],
                                                        },
                                                    }),
                                                )
                                            }}
                                        >
                                            Добавить в черный список
                                        </Button>
                                    ) : (
                                        <Button
                                            requiredPermission='delete'
                                            themeType='outline-2'
                                            onClick={() =>
                                                dispatch(
                                                    openModal({
                                                        isOpen: true,
                                                        content: (
                                                            <div className='blockWithComment'>
                                                                Вы уверены, что
                                                                хотите удалить
                                                                клиентов:
                                                                <br />
                                                                <b>
                                                                    {tab ===
                                                                    0 ? (
                                                                        <>
                                                                            {selectedRows
                                                                                .map(
                                                                                    item => {
                                                                                        const contact =
                                                                                            item.contact
                                                                                        return [
                                                                                            contact.surname,
                                                                                            contact.first_name,
                                                                                            contact.patronymic,
                                                                                        ]
                                                                                            .filter(
                                                                                                Boolean,
                                                                                            )
                                                                                            .join(
                                                                                                ' ',
                                                                                            )
                                                                                    },
                                                                                )
                                                                                .filter(
                                                                                    Boolean,
                                                                                )
                                                                                .join(
                                                                                    ', ',
                                                                                )}
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            {selectedRows.map(
                                                                                (
                                                                                    item,
                                                                                    key,
                                                                                ) => (
                                                                                    <div
                                                                                        key={
                                                                                            key
                                                                                        }
                                                                                    >
                                                                                        {item
                                                                                            .additional_data
                                                                                            ?.documents
                                                                                            ?.organization_name
                                                                                            ? item
                                                                                                  .additional_data
                                                                                                  .documents
                                                                                                  .organization_name
                                                                                            : [
                                                                                                  item
                                                                                                      .contact
                                                                                                      ?.surname,
                                                                                                  item
                                                                                                      .contact
                                                                                                      ?.first_name,
                                                                                                  item
                                                                                                      .contact
                                                                                                      ?.patronymic,
                                                                                              ]
                                                                                                  .filter(
                                                                                                      Boolean,
                                                                                                  )
                                                                                                  .join(
                                                                                                      ' ',
                                                                                                  )}
                                                                                    </div>
                                                                                ),
                                                                            )}
                                                                        </>
                                                                    )}
                                                                </b>
                                                                из черного
                                                                списка?
                                                            </div>
                                                        ),
                                                        config: {
                                                            title: 'Удалить из черного списка?',
                                                            btns: [
                                                                {
                                                                    onClick:
                                                                        () =>
                                                                            handleRemoveBlacList(
                                                                                selectedRows,
                                                                            ),
                                                                    title: 'Да',
                                                                    active: true,
                                                                },
                                                                {
                                                                    onClick:
                                                                        () =>
                                                                            dispatch(
                                                                                closeModal(),
                                                                            ),
                                                                    title: 'Нет',
                                                                    active: false,
                                                                },
                                                            ],
                                                        },
                                                    }),
                                                )
                                            }
                                        >
                                            Удалить из черного списка
                                        </Button>
                                    )}
                                    <Button
                                        requiredPermission='delete'
                                        themeType='delete'
                                        onClick={() => {
                                            dispatch(
                                                openModal({
                                                    isOpen: true,
                                                    content: (
                                                        <>
                                                            {tab !== 2 ? (
                                                                <>
                                                                    Вы удалите
                                                                    профиль{' '}
                                                                    {typeClient ===
                                                                    0
                                                                        ? 'физического лица'
                                                                        : 'юридического лица'}
                                                                    <br />
                                                                </>
                                                            ) : (
                                                                <>
                                                                    Вы удалите
                                                                    профиль
                                                                    клиентов:
                                                                    <br />
                                                                </>
                                                            )}
                                                            <b>
                                                                {tab === 0 ? (
                                                                    <>
                                                                        {selectedRows
                                                                            .map(
                                                                                item => {
                                                                                    const contact =
                                                                                        item.contact
                                                                                    return [
                                                                                        contact.surname,
                                                                                        contact.first_name,
                                                                                        contact.patronymic,
                                                                                    ]
                                                                                        .filter(
                                                                                            Boolean,
                                                                                        )
                                                                                        .join(
                                                                                            ' ',
                                                                                        )
                                                                                },
                                                                            )
                                                                            .filter(
                                                                                Boolean,
                                                                            )
                                                                            .join(
                                                                                ', ',
                                                                            )}
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        {selectedRows.map(
                                                                            (
                                                                                item,
                                                                                key,
                                                                            ) => (
                                                                                <div
                                                                                    key={
                                                                                        key
                                                                                    }
                                                                                >
                                                                                    {item
                                                                                        .additional_data
                                                                                        ?.documents
                                                                                        ?.organization_name
                                                                                        ? item
                                                                                              .additional_data
                                                                                              .documents
                                                                                              .organization_name
                                                                                        : [
                                                                                              item
                                                                                                  .contact
                                                                                                  ?.surname,
                                                                                              item
                                                                                                  .contact
                                                                                                  ?.first_name,
                                                                                              item
                                                                                                  .contact
                                                                                                  ?.patronymic,
                                                                                          ]
                                                                                              .filter(
                                                                                                  Boolean,
                                                                                              )
                                                                                              .join(
                                                                                                  ' ',
                                                                                              )}
                                                                                </div>
                                                                            ),
                                                                        )}
                                                                    </>
                                                                )}
                                                            </b>
                                                        </>
                                                    ),
                                                    config: {
                                                        title: 'Вы точно хотите удалить?',
                                                        btns: [
                                                            {
                                                                onClick: () =>
                                                                    handleDeleteClients(
                                                                        selectedRows,
                                                                    ),
                                                                title: 'Да',
                                                                active: true,
                                                                isLoading:
                                                                    loading,
                                                            },
                                                            {
                                                                onClick: () =>
                                                                    dispatch(
                                                                        closeModal(),
                                                                    ),
                                                                title: 'Нет',
                                                                active: false,
                                                            },
                                                        ],
                                                    },
                                                }),
                                            )
                                        }}
                                    >
                                        Удалить клиента
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Button
                                        requiredPermission='save'
                                        onClick={() =>
                                            handleClick(CLIENTSCREATE)
                                        }
                                    >
                                        + Добавить клиента
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                }
            >
                <TabsWrapper>
                    <Tabs onTabChange={handleTabChange}>
                        <Tab label='Физическое лицо'>
                            <WrapperSearch>
                                <Input
                                    name=''
                                    $beforeIcon='search'
                                    placeholder='Поиск'
                                    onChange={onSearch}
                                />
                            </WrapperSearch>
                            {isLoading ? (
                                <Loader />
                            ) : (
                                <>
                                    {data && (
                                        <Table
                                            className='clientsTable'
                                            data={data.counterparties}
                                            columns={columns}
                                            columnWidths={['40px', '69px']}
                                            selectedRows={selectedRows}
                                            onSelectionChange={
                                                handleSelectionChange
                                            }
                                            navigation={CLIENTSPROFILE}
                                            totalPages={
                                                data.pagination.total_pages
                                            }
                                            page={currentPage}
                                            onPageChange={page =>
                                                setSearchParams(
                                                    prev => {
                                                        const params =
                                                            new URLSearchParams(
                                                                prev,
                                                            )
                                                        params.set(
                                                            'page',
                                                            page.toString(),
                                                        )
                                                        return params
                                                    },
                                                    { replace: true },
                                                )
                                            }
                                            isLoading={isLoading}
                                            isError={isError}
                                            onSortChange={setSorting}
                                        />
                                    )}
                                </>
                            )}
                        </Tab>
                        <Tab label='Юридическое лицо'>
                            <WrapperSearch>
                                <Input
                                    name=''
                                    $beforeIcon='search'
                                    placeholder='Поиск'
                                    onChange={onSearch}
                                />
                            </WrapperSearch>
                            {isLoading ? (
                                <Loader />
                            ) : (
                                <>
                                    {data && (
                                        <Table
                                            className='clientsTable'
                                            data={data.counterparties}
                                            columns={columnsOrg}
                                            columnWidths={['40px', '69px']}
                                            selectedRows={selectedRows}
                                            onSelectionChange={
                                                handleSelectionChange
                                            }
                                            navigation={CLIENTSPROFILE}
                                            totalPages={
                                                data.pagination.total_pages
                                            }
                                            page={currentPage}
                                            onPageChange={page =>
                                                setSearchParams(
                                                    prev => {
                                                        const params =
                                                            new URLSearchParams(
                                                                prev,
                                                            )
                                                        params.set(
                                                            'page',
                                                            page.toString(),
                                                        )
                                                        return params
                                                    },
                                                    { replace: true },
                                                )
                                            }
                                            isLoading={isLoading}
                                            isError={isError}
                                            onSortChange={setSorting}
                                        />
                                    )}
                                </>
                            )}
                        </Tab>
                        <Tab label='Черный список'>
                            <WrapperSearch>
                                <Input
                                    name=''
                                    $beforeIcon='search'
                                    placeholder='Поиск'
                                    onChange={onSearch}
                                />
                            </WrapperSearch>
                            {isLoading ? (
                                <Loader />
                            ) : (
                                <>
                                    {data && (
                                        <Table
                                            className='clientsTable'
                                            data={data.counterparties}
                                            columns={columnBlackList}
                                            columnWidths={['40px', '69px']}
                                            selectedRows={selectedRows}
                                            onSelectionChange={
                                                handleSelectionChange
                                            }
                                            navigation={CLIENTSPROFILE}
                                            totalPages={
                                                data.pagination.total_pages
                                            }
                                            page={currentPage}
                                            onPageChange={page =>
                                                setSearchParams(
                                                    prev => {
                                                        const params =
                                                            new URLSearchParams(
                                                                prev,
                                                            )
                                                        params.set(
                                                            'page',
                                                            page.toString(),
                                                        )
                                                        return params
                                                    },
                                                    { replace: true },
                                                )
                                            }
                                            isLoading={isLoading}
                                            isError={isError}
                                            onSortChange={setSorting}
                                            disableSorting={false}
                                            disablePagination={false}
                                            disableSelection={false}
                                        />
                                    )}
                                </>
                            )}
                        </Tab>
                    </Tabs>
                </TabsWrapper>
            </DashboardLayout>
        </>
    )
}
